import React, { FC, useMemo } from 'react'
import { Box, makeStyles } from 'transfix-ui/core'
import { TIntegrationType } from 'apps/fleet-planner/types'
import { CheckFilledIcon } from 'transfix-icons'
import { Radio } from 'transfix-ui'
import { EldProviderLabel } from '../EldProviderRadioLabel/EldProviderRadioLabel'
import { IEldProviderFeature } from '../EldProviderRadioLabel/EldProvierRowFeatures'

interface IEldProvider {
  /**
   * @example 'KeepTruckin'
   */
  title: string
  /**
   * @example 'Connects with 750+ ELD providers'
   */
  subtitle?: string
  /**
   * Array of features for provider
   */
  features: IEldProviderFeature[]
}

const ELD_PROVIDERS: Partial<Record<TIntegrationType, IEldProvider>> = {
  keepTruckin: {
    title: 'KeepTruckin',
    features: [
      {
        Icon: CheckFilledIcon,
        color: 'info',
        title: 'Live Tracking for Transfix Loads',
      },
      {
        Icon: CheckFilledIcon,
        color: 'info',
        title: '24/7 Vehicle Tracking',
        subtitle: 'See where all your trucks are at any time, loaded or not.',
      },
      {
        Icon: CheckFilledIcon,
        color: 'info',
        title: 'Live Tracking for Custom Loads',
        subtitle: 'Track the location of any load you add to Transfix FMS.',
      },
    ],
  },
}

interface IEldProviderListProps {
  value: TIntegrationType | undefined
  onChange: (value: TIntegrationType) => void
}

const useEldProviderListStyles = makeStyles({
  /**
   * @note Temporary styles to hide radio elements until
   * we have more than one selection in this list
   */
  root: {
    '& .MuiRadio-colorPrimary': {
      display: 'none',
    },
    '& .MuiFormControlLabel-root': {
      marginLeft: 0,
    },
  },
})

/**
 * Lists selectable options of providers with <Radio /> inputs
 */
export const EldProviderList: FC<IEldProviderListProps> = ({ onChange, value }) => {
  const createChangeHandler = (keyName: TIntegrationType) => () => {
    onChange(keyName)
  }

  const keys = useMemo(() => Object.keys(ELD_PROVIDERS) as TIntegrationType[], [])

  const classes = useEldProviderListStyles()

  return (
    <Box data-test='eld-provider-list' className={classes.root}>
      {keys.map(keyName => {
        const val = ELD_PROVIDERS[keyName]
        const { title, subtitle, features = [] } = val || {}
        const isSelected = value === keyName
        return (
          <Box mb={3} key={title}>
            <Radio
              dataTest='eld-provider-list-item'
              value={keyName}
              checked={isSelected}
              onChange={createChangeHandler(keyName)}
              label={
                <EldProviderLabel
                  logoName={keyName}
                  title={title}
                  selected={isSelected}
                  features={features}
                  subtitle={subtitle}
                />
              }
            />
          </Box>
        )
      })}
    </Box>
  )
}
